import { StarIcon } from '@phosphor-icons/react';
import { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import SimpleLink from '_components/UI/SimpleLink';
import trackBazaarvoiceEvent from '@utils/gtm/ga4/trackBazaarvoiceEvents';

import styles from './ProductRatingStars.module.scss';

const STAR_COUNT = 5;
export interface ProductRatingStarsProps {
  rating: number;
  reviewCount?: number;
  showNumericRating?: boolean;
  showReviewCount?: boolean;
  className?: string;
  starSize?: 'ExtraSmall' | 'Small' | 'Medium' | 'Large';
  reviewsLink?: string;
  showWriteReviewPrompt?: boolean;
  scrollToProductReviewsAndAnswers?: () => void;
}

const getStarSize = (starSize: ProductRatingStarsProps['starSize']) => {
  switch (starSize) {
    case 'ExtraSmall':
      return 12;
    case 'Small':
      return 14;
    case 'Medium':
      return 16;
    case 'Large':
      return 20;
    default:
      return 14;
  }
};

const getStarSizeClass = (starSize: ProductRatingStarsProps['starSize']) => {
  switch (starSize) {
    case 'ExtraSmall':
      return styles.starSizeSmall;
    case 'Small':
      return styles.starSizeSmall;
    case 'Medium':
      return styles.starSizeMedium;
    case 'Large':
      return styles.starSizeLarge;
    default:
      return styles.starSizeSmall;
  }
};

const getReviewCountClass = (starSize: ProductRatingStarsProps['starSize']) => {
  switch (starSize) {
    case 'ExtraSmall':
      return styles.starSizeSmall;
    case 'Small':
      return styles.sizeSmall;
    case 'Medium':
      return styles.sizeMedium;
    case 'Large':
      return styles.sizeLarge;
    default:
      return styles.sizeSmall;
  }
};

export const ProductRatingStars = ({
  rating = 0,
  reviewCount,
  showNumericRating = false,
  showReviewCount = false,
  className,
  starSize = 'Small',
  reviewsLink = '',
  showWriteReviewPrompt = true,
  scrollToProductReviewsAndAnswers,
}: ProductRatingStarsProps): ReactElement => {
  const clampedRating = Math.min(STAR_COUNT, Math.max(0, rating));
  const starIconSize = getStarSize(starSize);
  const starSizeClass = getStarSizeClass(starSize);
  const reviewCountSizeClass = getReviewCountClass(starSize);
  const { t } = useTranslation('pdp');

  const linkSize = starSize === 'Small' ? 'small' : 'medium';
  const handleStarsClick = scrollToProductReviewsAndAnswers
    ? () => {
        trackBazaarvoiceEvent('Review stars');
        scrollToProductReviewsAndAnswers();
      }
    : undefined;
  const renderLinkOrText = (href: string | undefined, children: ReactNode) =>
    href ? (
      <SimpleLink
        href="#reviews-and-answers"
        variant="secondary"
        size={linkSize}
        onClick={handleStarsClick}
      >
        {children}
      </SimpleLink>
    ) : (
      <span className={styles.reviewCount}>{children}</span>
    );

  const stars: ReactElement[] = [];
  for (let i = 0; i < STAR_COUNT; i += 1) {
    const starsRemaining = clampedRating - i;

    if (starsRemaining >= 1) {
      stars.push(
        <span
          key={`star-${i}`}
          className={styles.star}
          data-testid="review-star-full"
          aria-hidden
        >
          <StarIcon size={starIconSize} weight="fill" />
        </span>
      );
    } else if (starsRemaining <= 0) {
      stars.push(
        <span
          key={`star-${i}`}
          className={styles.star}
          data-testid="review-star-empty"
          aria-hidden
        >
          <StarIcon
            size={starIconSize}
            weight="fill"
            color="var(--neutrals-300)"
          />
        </span>
      );
    } else {
      const fillWidthPercent = starsRemaining * 100;
      stars.push(
        <span
          key={`star-${i}`}
          className={styles.partialStarContainer}
          data-testid="review-star-partial"
          aria-hidden
        >
          <StarIcon
            size={starIconSize}
            weight="fill"
            color="var(--neutrals-300)"
            className={styles.partialStarOutline}
          />
          <span
            className={styles.partialStarFill}
            style={{ width: `${fillWidthPercent}%` }}
          >
            <StarIcon size={starIconSize} weight="fill" />
          </span>
        </span>
      );
    }
  }

  const showRatingBlock =
    showNumericRating || (showReviewCount && reviewCount != null);

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <div className={styles.ratingRow}>
        <div
          className={cn(styles.stars, starSizeClass)}
          role="img"
          aria-label={t('product.reviews.rating.aria', {
            rating: clampedRating.toFixed(1),
          })}
        >
          {stars}
        </div>
        {showRatingBlock && (
          <div className={cn(styles.ratingText, reviewCountSizeClass)}>
            {showNumericRating && reviewCount !== 0 && (
              <span className={styles.numericRating}>
                {clampedRating.toFixed(1)}
              </span>
            )}
            {showReviewCount &&
              reviewCount !== 0 &&
              renderLinkOrText(
                reviewsLink || undefined,
                reviewCount !== 1
                  ? t('product.reviews.count', { count: reviewCount })
                  : t('product.reviews.count.one', { count: reviewCount })
              )}
          </div>
        )}
        {reviewCount === 0 &&
          showWriteReviewPrompt &&
          renderLinkOrText(
            reviewsLink || undefined,
            t('product.write.a.review')
          )}
      </div>
    </div>
  );
};

export default ProductRatingStars;
